import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { ClientItemId } from 'owa-client-ids';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import { MailRibbonTabId, MailRibbonControlId } from 'owa-ribbon-ids';
import type { RibbonControlId } from 'owa-ribbon-ids';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import {
    getIsButtonDisabled,
    MutableControlProps,
    shouldShowRibbonControl,
} from 'owa-mail-ribbon-utils';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import type { TabViewState } from 'owa-tab-store';
import { getActiveContentTab, TabType } from 'owa-tab-store';
import { getIsPopoutButtonDisabled } from './getIsPopoutButtonDisabled';
import pushRibbonControl from './pushRibbonControl';

/**
 * This function returns an array of the controls to display.
 * For any logic on determining when to show/hide a control, please put it in the helper function `shouldShowRibbonControl`.
 * @param tabId MailRibbonTabId of the calling tab
 * @param controlIds list of all valid controls to be tested to see if should display
 * @param getControlDefinition function that returns the control definition given a controlId
 * @param props Properties for MailRibbonIdToControlMap
 * @returns array of RibbonControlProps which are the controls that should be shown in this group.
 */
export const getControlsToDisplay = computedFn(
    (
        tabId: MailRibbonTabId,
        controlIds: RibbonControlId[],
        getControlDefinition: (
            controlId: RibbonControlId,
            props: RibbonControlDefinitionProps
        ) => RibbonControlProps | undefined,
        mutableProps: MutableControlProps,
        props: RibbonControlDefinitionProps
    ): RibbonControlProps[] => {
        let menuItems: RibbonControlProps[] = new Array();

        for (const controlId of controlIds) {
            // In Ribbon, if a button is "not shown", we disable it. In Toolbar mode, we hide the button.
            if (
                isHiddenAndToolbarMode(
                    controlId,
                    props.popoutComposeViewState != undefined /*isPopout*/,
                    props.itemId
                )
            ) {
                continue;
            }

            if (shouldShowRibbonControl(tabId, controlId, mutableProps, props)) {
                pushRibbonControl(menuItems, controlId, getControlDefinition, props);
            }
        }

        return menuItems;
    }
);

// Checks if we are in Toolbar mode, and whether this controlId is hidden.
// One exception to this rule: Undo button when NOT in a secondary pane.
function isHiddenAndToolbarMode(
    controlId: RibbonControlId,
    isComposePopout: boolean,
    itemId?: ClientItemId
): boolean {
    if (!getIsViewModeSelected(CommandingViewMode.CommandBar)) {
        return false;
    }

    const shouldHideButton: boolean = itemId
        ? getIsPopoutButtonDisabled(controlId, itemId)
        : getIsButtonDisabled(controlId, isComposePopout);

    if (controlId !== MailRibbonControlId.Undo || !shouldHideButton) {
        return shouldHideButton;
    }

    // Disabled undo button in toolbar. Only hide if this is a secondary reading tab
    const activeTab: TabViewState = getActiveContentTab();

    return activeTab && activeTab.type == TabType.SecondaryReadingPane;
}
